/**
 * Floating AI assistant drawer: asks backend chat questions with optional chat/prospect context.
 *
 * Where: typically mounted from app shell when inbox/chat context exists (`AiHelper` barrel).
 * Uses: `chatApi` for completions; MUI `Drawer` + responsive layout.
 * Behavior: `visible` toggles FAB; without `context` the panel stays disabled; stores local chat transcript in state.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { chatApi } from '@/api/chat';

export interface AiHelperContext {
  chatUuid: string;
  prospectName?: string;
}

interface AiHelperFloatingButtonProps {
  /** When true, shows the floating button. When false, hides it. */
  visible?: boolean;
  /** Context for the AI (chat + prospect). When provided, enables asking questions. */
  context?: AiHelperContext | null;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  justification?: string;
}

export const AiHelperFloatingButton: React.FC<AiHelperFloatingButtonProps> = ({
  visible = true,
  context,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear messages when switching to another chat
  useEffect(() => {
    setMessages([]);
    setQuestion('');
    setError(null);
  }, [context?.chatUuid]);

  const showButton = visible && context?.chatUuid;

  const handleOpen = useCallback(() => {
    setOpen(true);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || !context?.chatUuid || isLoading) return;

    setQuestion('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: trimmedQuestion }]);
    setIsLoading(true);

    try {
      const result = await chatApi.askAiHelper(context.chatUuid, trimmedQuestion);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.answer ?? '',
          justification: result.justification,
        },
      ]);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to get AI response';
      setError(errMsg);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  }, [question, context?.chatUuid, isLoading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!showButton) return null;

  const drawerWidth = isMobile ? '100%' : 420;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          width: 56,
          height: 56,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: theme.shadows[8],
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[12],
          },
        }}
        aria-label="Ask AI about this chat"
      >
        <AiIcon sx={{ fontSize: 28 }} />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            maxWidth: '100%',
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AiIcon color="primary" />
              <Box>
                <Typography variant="h6" component="h2">
                  AI Helper
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {context.prospectName
                    ? `Asking about: ${context.prospectName}`
                    : 'Chat context'}
                  {' · '}
                  Chat: {context.chatUuid.slice(0, 8)}…
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                Ask a question about this prospect or request help writing a message.
                <br />
                <br />
                Examples: &quot;Write a follow-up message&quot;, &quot;Summarize our
                conversation&quot;, &quot;What is their current role?&quot;
              </Typography>
            )}
            {messages.map((msg, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 1.5,
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '90%',
                  backgroundColor:
                    msg.role === 'user'
                      ? theme.palette.primary.main
                      : theme.palette.grey[200],
                  color:
                    msg.role === 'user'
                      ? theme.palette.primary.contrastText
                      : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
                {msg.justification && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      opacity: 0.85,
                      fontStyle: 'italic',
                    }}
                  >
                    {msg.justification}
                  </Typography>
                )}
              </Paper>
            ))}
            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="caption" color="text.secondary">
                  Thinking…
                </Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask a question or request a message..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                size="small"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!question.trim() || isLoading}
                sx={{ minWidth: 48, height: 40 }}
              >
                <SendIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
